import { type JSONContent } from "novel"

type TextToken = {
  value: string;
  countable: boolean;
};


// 辅助函数：判断是否为英文字符
export const isEnglishChar = (char: string): boolean => {
  const charCode = char.charCodeAt(0);
  return (charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122);
};

const isDigitChar = (char: string): boolean => {
  const charCode = char.charCodeAt(0);
  return charCode >= 48 && charCode <= 57;
};

const isChineseChar = (char: string): boolean => {
  const charCode = char.charCodeAt(0);
  return (
    (charCode >= 0x4E00 && charCode <= 0x9FFF) || // CJK Unified Ideographs
    (charCode >= 0x3400 && charCode <= 0x4DBF)    // CJK Extension A
  );
};

const isWhitespaceChar = (char: string): boolean => /\s/.test(char);

const isPunctuationChar = (char: string): boolean => {
  if (!char) return false;
  const charCode = char.charCodeAt(0);
  // ASCII 标点
  if (
    (charCode >= 33 && charCode <= 47) ||
    (charCode >= 58 && charCode <= 64) ||
    (charCode >= 91 && charCode <= 96) ||
    (charCode >= 123 && charCode <= 126)
  ) {
    return true;
  }
  // 常见全角及中日韩标点
  if (
    (charCode >= 0x3000 && charCode <= 0x303F) || // CJK 标点
    (charCode >= 0xFF00 && charCode <= 0xFF0F) ||
    (charCode >= 0xFF1A && charCode <= 0xFF20) ||
    (charCode >= 0xFF3B && charCode <= 0xFF40) ||
    (charCode >= 0xFF5B && charCode <= 0xFF65) ||
    (charCode >= 0x2010 && charCode <= 0x2027) || // 破折号、省略号等
    (charCode >= 0x2E00 && charCode <= 0x2E7F)
  ) {
    return true;
  }
  return false;
};

const tokenizeTextForWordCount = (text: string): TextToken[] => {
  const tokens: TextToken[] = [];
  const chars = Array.from(text || '');
  let index = 0;

  while (index < chars.length) {
    const char = chars[index];

    if (isWhitespaceChar(char)) {
      tokens.push({ value: char, countable: false });
      index += 1;
      continue;
    }

    if (isEnglishChar(char)) {
      let end = index + 1;
      while (end < chars.length && isEnglishChar(chars[end])) {
        end += 1;
      }
      tokens.push({ value: chars.slice(index, end).join(''), countable: true });
      index = end;
      continue;
    }

    if (isDigitChar(char)) {
      let end = index + 1;
      while (end < chars.length && isDigitChar(chars[end])) {
        end += 1;
      }
      tokens.push({ value: chars.slice(index, end).join(''), countable: true });
      index = end;
      continue;
    }

    if (isPunctuationChar(char)) {
      tokens.push({ value: char, countable: false });
      index += 1;
      continue;
    }

    tokens.push({ value: char, countable: isChineseChar(char) || char.trim().length > 0 });
    index += 1;
  }

  return tokens;
};

const sliceTokensFromStart = (tokens: TextToken[], limit: number): string => {
  let count = 0;
  let startIndex = tokens.findIndex(token => token.countable);
  if (startIndex === -1) {
    return '';
  }

  let endIndex = tokens.length - 1;
  for (let i = startIndex; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.countable) {
      count += 1;
      if (count === limit) {
        endIndex = i;
        let nextIndex = i + 1;
        while (nextIndex < tokens.length && !tokens[nextIndex].countable) {
          endIndex = nextIndex;
          nextIndex += 1;
        }
        break;
      }
    }
  }

  return tokens.slice(startIndex, endIndex + 1).map(token => token.value).join('').trim();
};

const sliceTokensFromEnd = (tokens: TextToken[], limit: number): string => {
  let count = 0;
  let lastCountableIndex = -1;
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (tokens[i].countable) {
      lastCountableIndex = i;
      break;
    }
  }
  if (lastCountableIndex === -1) {
    return '';
  }

  let startIndex = 0;
  for (let i = lastCountableIndex; i >= 0; i--) {
    const token = tokens[i];
    if (token.countable) {
      count += 1;
      startIndex = i;
      if (count === limit) {
        let prevIndex = i - 1;
        while (prevIndex >= 0 && !tokens[prevIndex].countable) {
          startIndex = prevIndex;
          prevIndex -= 1;
        }
        break;
      }
    }
  }

  return tokens.slice(startIndex).map(token => token.value).join('').trim();
};


// 根据字数截取文本
// 如果direction为start，则从文本开头截取limit个单词或者字数
// 如果direction为end，则从文本末尾截取limit个单词或者字数
export const sliceTextByWordCount = (
  text: string,
  limit: number,
  direction: 'start' | 'end' = 'start'
): string => {
  if (!text) return '';
  const normalizedLimit = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 0;
  if (!normalizedLimit) return '';

  const tokens = tokenizeTextForWordCount(text);
  const totalCountable = tokens.reduce((sum, token) => sum + (token.countable ? 1 : 0), 0);

  if (normalizedLimit >= totalCountable) {
    return text.trim();
  }

  return direction === 'end'
    ? sliceTokensFromEnd(tokens, normalizedLimit)
    : sliceTokensFromStart(tokens, normalizedLimit);
};


// 字数统计结果类型
export interface WordCountResult {
  totalChars: number;        // 总字符数
  chineseChars: number;      // 中文字符数
  englishWords: number;      // 英文单词数
  spaces: number;           // 空格数
  specialChars: number;     // 特殊字符数
  paragraphs: number;       // 段落数
  lines: number;           // 行数
}

// 获取tiptap的json 字数
// 递归 累加text类型的字数
// 并排除类型aiGeneration
// 统计 空格个数，特殊字符个数，中文字符个数，英文单词个数
export const getTiptapWordCount = (json: JSONContent): WordCountResult => {
  const result: WordCountResult = {
    totalChars: 0,
    chineseChars: 0,
    englishWords: 0,
    spaces: 0,
    specialChars: 0,
    paragraphs: 0,
    lines: 0
  };

  // 递归遍历节点
  const traverseNode = (node: JSONContent) => {
    // 跳过 AI 生成的内容
    if (node.type === 'aiGeneration') {
      return;
    }

    // 统计段落
    if (node.type === 'paragraph') {
      result.paragraphs++;
    }

    // 统计行数（硬换行）
    if (node.type === 'hardBreak') {
      result.lines++;
    }

    // 处理文本内容
    if (node.type === 'text' && node.text) {
      const text = node.text;
      result.totalChars += text.length;

      // 统计各种字符类型
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const charCode = char.charCodeAt(0);

        if (char === ' ') {
          result.spaces++;
        } else if (char === '\n') {
          result.lines++;
        } else if (charCode >= 0x4e00 && charCode <= 0x9fff) {
          // 中文字符范围
          result.chineseChars++;
        } else if ((charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122)) {
          // 英文字母
          // 检查是否是单词的开始
          if (i === 0 || !isEnglishChar(text[i - 1])) {
            result.englishWords++;
          }
        } else if (charCode >= 33 && charCode <= 126) {
          // 可打印的ASCII特殊字符
          result.specialChars++;
        }
      }
    }

    // 递归处理子节点
    if (node.content) {
      node.content.forEach(traverseNode);
    }
  };



  // 开始遍历
  traverseNode(json);

  return result;
}

// 新增：统计字符串字数的函数
export const getStringWordCount = (text: string): number => {
  let chineseChars = 0;
  let englishWords = 0;
  let specialChars = 0;

  // 统计各种字符类型
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const charCode = char.charCodeAt(0);

    if (charCode >= 0x4e00 && charCode <= 0x9fff) {
      // 中文字符范围
      chineseChars++;
    } else if ((charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122)) {
      // 英文字母
      // 检查是否是单词的开始
      if (i === 0 || !isEnglishChar(text[i - 1])) {
        englishWords++;
      }
    } else if (charCode >= 33 && charCode <= 126 && char !== ' ') {
      // 可打印的ASCII特殊字符（排除空格）
      specialChars++;
    }
  }

  return chineseChars + englishWords;
}

// 获取简化的字数统计（仅返回总字符数）
export const getTiptapSimpleWordCount = (data?: JSONContent | string): number => {
  if (!data) return 0;
  if (typeof data === 'string') {
    return getStringWordCount(data);
  }
  const result = getTiptapWordCount(data);
  return result.chineseChars + result.englishWords;
}